import React, { useState } from 'react';
import { Button, Input, message, Modal, Select, Table, Tooltip, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { bulkAddMCPServerTools } from '../MCPServerApis';
import { validateToolRow } from '../ToolsHelper';

const { Text } = Typography;

const booleanSelect = (sharedProps) => (
    <Select
        style={{ width: 70 }}
        options={[
            { value: '1', label: '1' },
            { value: '0', label: '0' },
        ]}
        {...sharedProps}
    />
);

const ToolsImportModal = ({ open, rows: initialRows, onClose, onSuccess }) => {
    const [rows, setRows] = useState(initialRows);
    const [submitting, setSubmitting] = useState(false);

    React.useEffect(() => {
        setRows(initialRows);
        setSubmitting(false);
    }, [initialRows, open]);

    const updateRow = (key, field, value) => {
        setRows((prev) =>
            prev.map((row) => {
                if (row._key !== key) {
                    return row;
                }
                const updated = { ...row, [field]: value };
                updated._errors = validateToolRow(updated);
                return updated;
            })
        );
    };

    const removeRow = (key) => {
        setRows((prev) => prev.filter((row) => row._key !== key));
    };

    const validRows = rows.filter((row) => Object.keys(row._errors ?? {}).length === 0);
    const invalidCount = rows.length - validRows.length;

    const onSubmit = async () => {
        if (validRows.length === 0) {
            return;
        }
        setSubmitting(true);
        const payload = validRows.map(({ _key, _errors, ...tool }) => tool);
        try {
            const result = await bulkAddMCPServerTools(payload);
            message.success(
                `Imported ${result.inserted} tool(s) across ${result.serversReplaced.length} server(s)`
            );
            onSuccess();
            onClose();
        } catch (error) {
            if (error.rowErrors?.length) {
                setRows((prev) =>
                    prev.map((row) => {
                        const serverError = error.rowErrors.find((e) => e.clientRowId === row.clientRowId);
                        if (serverError) {
                            return {
                                ...row,
                                _errors: { ...row._errors, mcp_server_id: serverError.message },
                            };
                        }
                        return row;
                    })
                );
                message.error(`Import rejected: ${error.rowErrors[0].message}`);
            } else {
                message.error(error.message ?? 'Failed to import tools');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const columns = [
        {
            title: 'Server ID',
            dataIndex: 'mcp_server_id',
            width: 110,
            render: (value) => <Text>{value}</Text>,
        },
        {
            title: 'Tool Name',
            dataIndex: 'tool_name',
            width: 180,
            render: (value, row) => (
                <Input
                    value={value}
                    status={row._errors?.tool_name ? 'error' : undefined}
                    onChange={(e) => updateRow(row._key, 'tool_name', e.target.value)}
                />
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            width: 200,
            render: (value, row) => (
                <Input
                    value={value}
                    onChange={(e) => updateRow(row._key, 'description', e.target.value)}
                />
            ),
        },
        {
            title: 'Parameters',
            dataIndex: 'parameters',
            width: 200,
            render: (value, row) => (
                <Input
                    value={value}
                    onChange={(e) => updateRow(row._key, 'parameters', e.target.value)}
                />
            ),
        },
        {
            title: 'Read',
            dataIndex: 'supports_read',
            width: 90,
            render: (value, row) =>
                booleanSelect({
                    value: ['0', '1'].includes(value) ? value : undefined,
                    status: row._errors?.supports_read ? 'error' : undefined,
                    onChange: (v) => updateRow(row._key, 'supports_read', v ?? ''),
                }),
        },
        {
            title: 'Write',
            dataIndex: 'supports_write',
            width: 90,
            render: (value, row) =>
                booleanSelect({
                    value: ['0', '1'].includes(value) ? value : undefined,
                    status: row._errors?.supports_write ? 'error' : undefined,
                    onChange: (v) => updateRow(row._key, 'supports_write', v ?? ''),
                }),
        },
        {
            title: 'Delete',
            dataIndex: 'supports_delete',
            width: 90,
            render: (value, row) =>
                booleanSelect({
                    value: ['0', '1'].includes(value) ? value : undefined,
                    status: row._errors?.supports_delete ? 'error' : undefined,
                    onChange: (v) => updateRow(row._key, 'supports_delete', v ?? ''),
                }),
        },
        {
            title: 'Errors',
            dataIndex: '_errors',
            width: 150,
            render: (errors) => {
                const messages = Object.values(errors ?? {});
                if (messages.length === 0) {
                    return <Text type='success'>Valid</Text>;
                }
                return (
                    <Tooltip title={messages.join('\n')}>
                        <Text type='danger' style={{ whiteSpace: 'pre-line' }}>
                            {messages.join('; ')}
                        </Text>
                    </Tooltip>
                );
            },
        },
        {
            title: '',
            dataIndex: 'actions',
            width: 50,
            render: (_, row) => (
                <Button
                    type='text'
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeRow(row._key)}
                />
            ),
        },
    ];

    return (
        <Modal
            title='Import Tools from CSV'
            open={open}
            footer={false}
            onCancel={onClose}
            width={'90%'}
        >
            <div style={{ marginBottom: 12 }}>
                <Text>
                    {rows.length} row(s): {validRows.length} valid, {invalidCount} with errors.
                    {' '}
                    {invalidCount > 0 && <Text type='danger'>Rows with errors are excluded on submit.</Text>}
                </Text>
            </div>
            <Table
                rowKey='_key'
                size='small'
                bordered
                dataSource={rows}
                columns={columns}
                pagination={false}
                scroll={{ x: true, y: 400 }}
            />
            <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Button style={{ marginRight: 8 }} onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    type='primary'
                    loading={submitting}
                    disabled={validRows.length === 0}
                    onClick={onSubmit}
                >
                    Submit ({validRows.length})
                </Button>
            </div>
        </Modal>
    );
};

export default ToolsImportModal;
